import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { TagSearchMenu } from 'shared/components/ui/tag-search-menu';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes';
import { InteractionType } from 'shared/types/data-type';
import { ID } from 'shared/types/id';
import { NewTagWithID, Tag as TagType } from 'shared/types/tag';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './task-meta-interaction-tags.style';

export type TaskMetaInteractionTagsProps = {
  tags: (TagType | NewTagWithID)[];
  value?: ID[] | null;
  onChange: (ids: ID[]) => void;
  onCreate?: (name: string) => void;
  highlight?: boolean;
  redirectToTag?: boolean;
};

export const TaskMetaInteractionTags: React.FC<
  TaskMetaInteractionTagsProps
> = ({ tags, value, onChange, onCreate, highlight, redirectToTag }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const items = useMemo(
    () => tags.filter((tag) => value?.includes(tag.id)),
    [tags, value],
  );

  const _onCreate = (name: string) => {
    onCreate?.(name);
    !name && closeMenu();
  };

  const navigateToTag = (id: ID) => {
    closeMenu();
    navigate(replaceUrlParams(Paths.TagDetail, { id }));
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [menuOpen]);

  return (
    <Styled.Container ref={containerRef}>
      {items.length ? (
        items.map((item) => {
          const onClick = (e: React.MouseEvent) => {
            if (redirectToTag) {
              e.stopPropagation();
              navigateToTag(item.id);
              return;
            }
            openMenu();
          };

          return (
            <TaskMetaInteraction
              key={item.id}
              start={
                <Styled.IconContainer $color={item.color}>
                  <Icon icon={Tag} />
                </Styled.IconContainer>
              }
              color={highlight ? Colors.TextPrimary : Colors.Inherit}
              label={item.name}
              data-id={item.id}
              onClick={onClick}
              selected={menuOpen}
            />
          );
        })
      ) : (
        <TaskMetaInteraction
          start={
            <Styled.IconContainer>
              <Icon icon={Tag} />
            </Styled.IconContainer>
          }
          onClick={openMenu}
          selected={menuOpen}
        />
      )}

      {menuOpen && (
        <TagSearchMenu
          tags={tags}
          onChange={onChange}
          onCreate={onCreate ? _onCreate : undefined}
          value={value}
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          onOutside={closeMenu}
        />
      )}
    </Styled.Container>
  );
};
